import React, { useRef, useState, useEffect, useCallback } from "react";
import { GALLERY } from "@/site.config";
import { MoveHorizontal, ChevronLeft, ChevronRight } from "lucide-react";

function BeforeAfterSlider({ before, after, title, beforePosition = "center", afterPosition = "center" }) {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50);
  const draggingRef = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const next = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(2, Math.min(98, next)));
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      setFromClientX(x);
    };
    const stop = () => (draggingRef.current = false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("mouseup", stop);
    window.addEventListener("touchend", stop);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("touchend", stop);
    };
  }, [setFromClientX]);

  const start = (e) => {
    draggingRef.current = true;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    setFromClientX(x);
  };

  return (
    <div
      ref={containerRef}
      data-testid="before-after-slider"
      className="relative w-full aspect-[16/10] rounded-2xl overflow-hidden border border-white/10 select-none cursor-ew-resize bg-black"
      onMouseDown={start}
      onTouchStart={start}
    >
      <img src={after} alt="Après" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: afterPosition }} />
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${pos}%` }}
      >
        <img
          src={before}
          alt="Avant"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ width: `${100 / (pos / 100)}%`, maxWidth: "none", objectPosition: beforePosition }}
        />
      </div>

      {/* Labels */}
      <div className="absolute top-4 left-4 px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-wider bg-black/70 border border-white/15 text-white">
        Avant
      </div>
      <div className="absolute top-4 right-4 px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-wider bg-[#9DEB30]/15 border border-[#9DEB30]/40 text-[#9DEB30]">
        Après
      </div>
      {title && (
        <div className="absolute bottom-4 left-4 right-4 text-sm text-white/90 font-medium">
          {title}
        </div>
      )}

      {/* Divider */}
      <div
        className="absolute top-0 bottom-0 w-px bg-[#9DEB30] shadow-[0_0_20px_rgba(157,235,48,0.6)]"
        style={{ left: `${pos}%` }}
      />
      <div
        className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-[#9DEB30] flex items-center justify-center text-black neon-glow-soft"
        style={{ left: `${pos}%` }}
      >
        <MoveHorizontal size={18} />
      </div>
    </div>
  );
}

export default function BeforeAfter() {
  const [index, setIndex] = useState(0);
  const item = GALLERY[index];
  const hasMultiple = GALLERY.length > 1;

  const prev = () => setIndex((i) => (i - 1 + GALLERY.length) % GALLERY.length);
  const next = () => setIndex((i) => (i + 1) % GALLERY.length);

  return (
    <section
      id="avant-apres"
      data-testid="before-after-section"
      className="relative py-24 sm:py-32 px-5 sm:px-8"
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#9DEB30] mb-3">
            · Avant / Après
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
            La différence se voit{" "}
            <span className="text-[#9DEB30]">au premier coup d'œil.</span>
          </h2>
          <p className="mt-4 text-zinc-400 max-w-2xl mx-auto">
            Glissez le curseur pour révéler le rendu après intervention.
          </p>
        </div>

        <div className="relative">
          <BeforeAfterSlider
            key={item.id}
            before={item.before}
            after={item.after}
            title={item.title}
            beforePosition={item.beforePosition}
            afterPosition={item.afterPosition}
          />

          {hasMultiple && (
            <>
              <button
                data-testid="ba-prev"
                onClick={prev}
                aria-label="Slide précédent"
                className="absolute left-2 sm:-left-5 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-black/70 border border-white/10 text-white flex items-center justify-center hover:border-[#9DEB30]/60 hover:text-[#9DEB30] transition z-10"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                data-testid="ba-next"
                onClick={next}
                aria-label="Slide suivant"
                className="absolute right-2 sm:-right-5 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-black/70 border border-white/10 text-white flex items-center justify-center hover:border-[#9DEB30]/60 hover:text-[#9DEB30] transition z-10"
              >
                <ChevronRight size={20} />
              </button>
            </>
          )}
        </div>

        <div className="mt-6 flex items-center justify-center gap-3 text-xs font-mono text-zinc-500">
          <span className="h-px w-8 bg-zinc-700" />
          Glissez pour comparer
          <span className="h-px w-8 bg-zinc-700" />
        </div>

        {hasMultiple && (
          <div className="mt-6 flex items-center justify-center gap-2">
            {GALLERY.map((g, i) => (
              <button
                key={g.id}
                data-testid={`ba-dot-${i}`}
                onClick={() => setIndex(i)}
                aria-label={`Slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === index ? "w-8 bg-[#9DEB30]" : "w-2 bg-zinc-700 hover:bg-zinc-500"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
